import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'

export function DropdownMenu({
  trigger,
  children,
  align = 'end',
  className,
  panelClassName,
}: {
  trigger: React.ReactNode
  children: React.ReactNode
  align?: 'start' | 'end'
  className?: string
  panelClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className={cn('relative inline-flex', className)}>
      <div onClick={() => setOpen(v => !v)}>{trigger}</div>
      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute top-full z-50 mt-1 min-w-[10rem] rounded-lg glass-elevated p-1 shadow-lg',
            align === 'end' ? 'right-0' : 'left-0',
            panelClassName,
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

export function DropdownMenuItem({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode
  onClick?: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center rounded-md px-2 py-1.5 text-sm hover:bg-muted',
        className
      )}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function DropdownMenuTrigger({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <Button variant="outline" size="sm" className={cn('gap-1', className)}>
      {children}
      <ChevronDown className="size-3.5 opacity-60" />
    </Button>
  )
}
